/**
 * Modal "Custos de <empresa>": uso x tarifa por canal, total, comissao e
 * margem da empresa no periodo.
 *
 * Decisoes nao obvias:
 *  1. E um modal, nao uma linha expandida: o detalhe tem quatro linhas de
 *     uso e tarifa e uma conclusao; aberto em cada linha da tabela viraria
 *     uma segunda tabela dentro da primeira.
 *  2. A linha do WhatsApp muda com a ROTA da empresa: pela API oficial o
 *     custo e por conversa; pela Evolution e a instancia rateada por dia,
 *     mesmo sem mensagem. E a diferenca que explica uma margem negativa
 *     numa empresa parada.
 *  3. As tarifas aparecem por extenso ao lado do uso, para qualquer pessoa
 *     conferir o custo na calculadora.
 *  4. Sem custo medido (motor real) o modal mostra so o USO e a comissao, e
 *     diz por escrito que custo e margem ainda nao sao medidos. Nenhum R$ 0,00.
 */

import { Badge, Footnote, Modal } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatPercentFromBps, VALOR_AUSENTE } from '../../../lib/format';
import type { CompanyRow, CostByChannel, CostChannel, CostTariffs } from '../types';
import { COST_CHANNEL_DOT, COST_CHANNEL_LABEL, COST_CHANNEL_ORDER, WHATSAPP_ROUTE_LABEL } from '../vocabulario';

interface LinhaDeCusto {
  readonly canal: CostChannel;
  readonly uso: string;
  readonly tarifa: string;
  readonly custoCents: number;
}

function linhasDe(row: CompanyRow, c: CostByChannel, t: CostTariffs): readonly LinhaDeCusto[] {
  const u = row.usage;
  const whatsapp: LinhaDeCusto =
    row.whatsappRoute === 'meta_oficial'
      ? {
          canal: 'whatsapp',
          uso: `${u.whatsappConversations === null ? 'conversas não medidas' : `${formatCount(u.whatsappConversations)} conversas`} · ${formatCount(u.whatsappMessages)} mensagens`,
          tarifa: `${formatBRLFromCents(t.whatsappOfficialPerConversationCents)} por conversa (API oficial da Meta)`,
          custoCents: c.whatsappCents,
        }
      : {
          canal: 'whatsapp',
          uso: `${formatCount(u.whatsappMessages)} mensagens · 1 instância`,
          tarifa: `${formatBRLFromCents(t.whatsappEvolutionMonthlyPerInstanceCents)} por mês por instância, rateados por dia (Evolution API)`,
          custoCents: c.whatsappCents,
        };
  return [
    {
      canal: 'voice',
      uso: `${formatCount(u.voiceCalls)} ligações (${formatCount(u.voiceAnswered)} atendidas) · ${formatCount(u.voiceMinutes)} min cobrados`,
      tarifa: `${formatBRLFromCents(t.voicePerMinuteCents)} por minuto (Vapi)`,
      custoCents: c.voiceCents,
    },
    whatsapp,
    {
      canal: 'email',
      uso: `${formatCount(u.emailsSent)} e-mails enviados`,
      tarifa: `${formatBRLFromCents(t.emailPerThousandCents)} por mil (provedor a definir)`,
      custoCents: c.emailCents,
    },
    {
      canal: 'infra',
      uso: 'Rateio por volume entre as empresas ativas',
      tarifa: `servidor ${formatBRLFromCents(t.infraServerMonthlyCents)} + banco ${formatBRLFromCents(t.infraDatabaseMonthlyCents)} por mês`,
      custoCents: c.infraCents,
    },
  ];
}

export function DetalheDeCustos({
  row,
  tariffs,
  periodo,
  onClose,
}: {
  readonly row: CompanyRow | null;
  readonly tariffs: CostTariffs | null;
  /** Texto da janela do cabecalho, ex.: "Últimos 30 dias". */
  readonly periodo: string;
  readonly onClose: () => void;
}): JSX.Element | null {
  if (row === null) {
    return null;
  }
  const custo = row.costByChannel;
  if (custo === null || tariffs === null || row.costCents === null || row.marginCents === null) {
    const u = row.usage;
    return (
      <Modal open onClose={onClose} title={`Uso de ${row.companyName}`} description={`Uso dos canais em ${periodo.toLowerCase()}. Custo e margem ainda não são medidos pelo motor.`} size="lg">
        <div className="flex flex-col gap-4">
          <dl className="grid gap-2 text-sm sm:grid-cols-3">
            <div className="rounded-xl border border-line bg-elevated px-3 py-2.5">
              <dt className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">{COST_CHANNEL_LABEL.voice}</dt>
              <dd className="text-lg font-semibold tabular-nums text-ink">{formatCount(u.voiceCalls)} ligações</dd>
              <dd className="text-xs text-ink-subtle">
                {formatCount(u.voiceAnswered)} atendidas · {formatCount(u.voiceMinutes)} min
              </dd>
            </div>
            <div className="rounded-xl border border-line bg-elevated px-3 py-2.5">
              <dt className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">{COST_CHANNEL_LABEL.whatsapp}</dt>
              <dd className="text-lg font-semibold tabular-nums text-ink">{formatCount(u.whatsappMessages)} mensagens</dd>
              <dd className="text-xs text-ink-subtle">pela {WHATSAPP_ROUTE_LABEL[row.whatsappRoute]}</dd>
            </div>
            <div className="rounded-xl border border-line bg-elevated px-3 py-2.5">
              <dt className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Comissão</dt>
              <dd className="text-lg font-semibold tabular-nums text-ink">{formatBRLFromCents(row.commissionCents)}</dd>
              <dd className="text-xs text-ink-subtle">{formatPercentFromBps(row.feeBps)} sobre o recuperado</dd>
            </div>
          </dl>
          <Footnote>
            O motor ainda não lê as faturas da Vapi, da Evolution, do servidor nem do banco, então custo e margem ficam em branco em vez de R$ 0,00. Taxa de recuperação:{' '}
            {row.abandonedCount === 0 ? VALOR_AUSENTE : formatPercentFromBps(row.recoveryRateBps)}.
          </Footnote>
        </div>
      </Modal>
    );
  }
  const marginCents = row.marginCents;
  const linhas = linhasDe(row, custo, tariffs);
  const ordenadas = COST_CHANNEL_ORDER.map((c) => linhas.find((l) => l.canal === c)).filter((l): l is LinhaDeCusto => l !== undefined);

  return (
    <Modal
      open
      onClose={onClose}
      title={`Custos de ${row.companyName}`}
      description={`Uso × tarifa por canal em ${periodo.toLowerCase()}. WhatsApp pela ${WHATSAPP_ROUTE_LABEL[row.whatsappRoute]}.`}
      size="lg"
    >
      <div className="flex flex-col gap-4">
        <div className="overflow-x-auto rounded-xl border border-line">
          <table className="w-full border-collapse text-sm">
            <caption className="sr-only">Custo por canal: uso, tarifa e valor no período.</caption>
            <thead>
              <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-ink-subtle">
                <th scope="col" className="px-3 py-2 font-medium">
                  Canal
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  Uso no período
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  Tarifa
                </th>
                <th scope="col" className="px-3 py-2 text-right font-medium">
                  Custo
                </th>
              </tr>
            </thead>
            <tbody>
              {ordenadas.map((l) => (
                <tr key={l.canal} className="border-b border-line/60 last:border-0">
                  <th scope="row" className="px-3 py-2 text-left font-medium text-ink">
                    <span className="flex items-center gap-2">
                      <span aria-hidden="true" className={`h-2 w-2 shrink-0 rounded-full ${COST_CHANNEL_DOT[l.canal]}`} />
                      {COST_CHANNEL_LABEL[l.canal]}
                    </span>
                  </th>
                  <td className="px-3 py-2 text-ink-muted">{l.uso}</td>
                  <td className="px-3 py-2 text-xs text-ink-subtle">{l.tarifa}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-ink">{formatBRLFromCents(l.custoCents)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-line bg-elevated text-xs">
                <th scope="row" colSpan={3} className="px-3 py-2 text-left font-medium text-ink-muted">
                  Total de custos
                </th>
                <td className="px-3 py-2 text-right font-semibold tabular-nums text-ink">{formatBRLFromCents(row.costCents)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <dl className="grid grid-cols-3 gap-2 text-sm">
          <div className="rounded-xl border border-line bg-elevated px-3 py-2.5">
            <dt className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Comissão</dt>
            <dd className="text-lg font-semibold tabular-nums text-ink">{formatBRLFromCents(row.commissionCents)}</dd>
            <dd className="text-xs text-ink-subtle">{formatPercentFromBps(row.feeBps)} sobre o recuperado</dd>
          </div>
          <div className="rounded-xl border border-line bg-elevated px-3 py-2.5">
            <dt className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Custos</dt>
            <dd className="text-lg font-semibold tabular-nums text-ink">{formatBRLFromCents(row.costCents)}</dd>
            <dd className="text-xs text-ink-subtle">{formatCount(row.abandonedCount)} entradas trabalhadas</dd>
          </div>
          <div className="rounded-xl border border-line bg-elevated px-3 py-2.5">
            <dt className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Margem</dt>
            <dd className={`text-lg font-semibold tabular-nums ${marginCents < 0 ? 'text-status-critical' : 'text-status-good'}`}>{formatBRLFromCents(marginCents)}</dd>
            <dd className="text-xs text-ink-subtle">{row.marginBps === null ? 'sem comissão no período' : `${formatPercentFromBps(row.marginBps)} da comissão`}</dd>
          </div>
        </dl>

        {marginCents < 0 ? (
          <Badge tone="critical" dot>
            Custos acima da comissão no período
          </Badge>
        ) : null}

        <Footnote>
          {tariffs.source === 'exemplo'
            ? 'Tarifas de exemplo, fixas na prévia; as reais vêm das faturas. '
            : 'Tarifas lidas das faturas. '}
          Custo nunca é repassado à empresa: ela vê só o retorno (recuperado, comissão e líquido). Taxa de recuperação: {row.abandonedCount === 0 ? VALOR_AUSENTE : formatPercentFromBps(row.recoveryRateBps)}.
        </Footnote>
      </div>
    </Modal>
  );
}
